import React, { useState, useEffect } from 'react'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Calendar, Users, Globe, Clock, TrendingUp, Loader } from 'lucide-react'
import { analyticsAPI, formatters } from '../services/analytics'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
)

function Overview() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [dateRange, setDateRange] = useState('7daysAgo')

  useEffect(() => {
    fetchOverviewData()
  }, [dateRange])

  const fetchOverviewData = async () => {
    try {
      setLoading(true)
      setError(null)
      const overview = await analyticsAPI.getOverview(dateRange)
      setData(overview)
    } catch (err) {
      setError(err.message)
      console.error('Erro ao buscar dados de overview:', err)
    } finally {
      setLoading(false)
    }
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return formatters.number(value)
          }
        }
      }
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
  }

  const getChartData = () => {
    if (!data?.current?.dailyData) return null

    const labels = data.current.dailyData.map(item => 
      new Date(item.date).toLocaleDateString('pt-BR', { 
        month: 'short', 
        day: 'numeric' 
      })
    )

    return {
      labels,
      datasets: [
        {
          label: 'Sessões',
          data: data.current.dailyData.map(item => item.sessions),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
        },
        {
          label: 'Usuários',
          data: data.current.dailyData.map(item => item.users),
          borderColor: 'rgb(16, 185, 129)',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          fill: true,
        },
        {
          label: 'Pageviews',
          data: data.current.dailyData.map(item => item.pageviews),
          borderColor: 'rgb(139, 92, 246)',
          backgroundColor: 'rgba(139, 92, 246, 0.1)',
          fill: true,
        }
      ],
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <Loader className="w-6 h-6 animate-spin text-primary-600" />
          <span className="text-lg text-gray-600">Carregando dados...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <h3 className="text-lg font-medium text-red-800 mb-2">Erro ao carregar dados</h3>
        <p className="text-red-600">{error}</p>
        <button 
          onClick={fetchOverviewData}
          className="mt-4 btn-primary"
        >
          Tentar novamente
        </button>
      </div>
    )
  }

  const chartData = getChartData()

  return (
    <div className="space-y-8">
      {/* Header com filtros */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Visão Geral</h1>
          <p className="mt-2 text-gray-600">Métricas principais e tendências</p>
        </div>
        <div className="mt-4 sm:mt-0">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="block w-full sm:w-auto px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="today">Hoje</option>
            <option value="yesterday">Ontem</option>
            <option value="7daysAgo">Últimos 7 dias</option>
            <option value="30daysAgo">Últimos 30 dias</option>
            <option value="90daysAgo">Últimos 90 dias</option>
          </select>
        </div>
      </div>

      {/* Métricas principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total de Sessões"
          value={data?.current?.summary?.totalSessions || 0}
          change={data?.changes?.sessions || 0}
          icon={Users}
          color="text-blue-600"
          bgColor="bg-blue-50"
        />
        <MetricCard
          title="Total de Usuários"
          value={data?.current?.summary?.totalUsers || 0}
          change={data?.changes?.users || 0}
          icon={Globe}
          color="text-green-600"
          bgColor="bg-green-50"
        />
        <MetricCard
          title="Pageviews"
          value={data?.current?.summary?.totalPageviews || 0}
          change={data?.changes?.pageviews || 0}
          icon={Calendar}
          color="text-purple-600"
          bgColor="bg-purple-50"
        />
        <MetricCard
          title="Duração Média"
          value={formatters.duration(data?.current?.summary?.avgSessionDuration || 0)}
          change={data?.changes?.averageSessionDuration || 0}
          icon={Clock}
          color="text-orange-600"
          bgColor="bg-orange-50"
        />
      </div>

      {/* Gráfico principal */}
      <div className="card">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Tendência de Tráfego</h3>
          <p className="text-gray-600">Evolução das métricas ao longo do tempo</p>
        </div>
        <div className="h-96">
          {chartData ? (
            <Line options={chartOptions} data={chartData} />
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-500">Dados não disponíveis</p>
            </div>
          )}
        </div>
      </div>

      {/* Métricas adicionais */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Resumo do Período</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Taxa de Rejeição</span>
              <span className="text-lg font-semibold text-gray-900">
                {formatters.percentage(data?.current?.summary?.bounceRate || 0)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Taxa de Novos Usuários</span>
              <span className="text-lg font-semibold text-gray-900">
                {formatters.percentage(data?.current?.summary?.newUserRate || 0)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Sessões por Usuário</span>
              <span className="text-lg font-semibold text-gray-900">
                {(data?.current?.totals?.sessionsPerUser || 0).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Período Analisado</span>
              <span className="text-lg font-semibold text-gray-900">
                {data?.dateRange || 'N/A'}
              </span>
            </div>
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Comparação</h3>
          <div className="space-y-4">
            {data?.changes && Object.entries(data.changes).map(([key, value]) => (
              <div key={key} className="flex justify-between items-center">
                <span className="text-gray-600 capitalize">
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </span>
                <div className={`flex items-center ${
                  value > 0 ? 'text-green-600' : value < 0 ? 'text-red-600' : 'text-gray-600'
                }`}>
                  <TrendingUp className={`w-4 h-4 mr-1 ${
                    value < 0 ? 'transform rotate-180' : ''
                  }`} />
                  <span className="font-semibold">{Math.abs(value)}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Última atualização */}
      <div className="text-center text-sm text-gray-500">
        Última atualização: {data?.lastUpdated ? 
          formatters.dateTime(data.lastUpdated) : 'N/A'
        }
      </div>
    </div>
  )
}

function MetricCard({ title, value, change, icon: Icon, color, bgColor }) {
  const isPositive = change > 0
  const isNegative = change < 0

  return (
    <div className="metric-card">
      <div className="flex items-center justify-between">
        <div className={`p-3 rounded-lg ${bgColor}`}>
          <Icon className={`w-6 h-6 ${color}`} />
        </div>
        {change !== undefined && (
          <div className={`flex items-center text-sm ${
            isPositive ? 'text-green-600' : isNegative ? 'text-red-600' : 'text-gray-600'
          }`}>
            <TrendingUp className={`w-4 h-4 mr-1 ${
              isNegative ? 'transform rotate-180' : ''
            }`} />
            {Math.abs(change)}%
          </div>
        )}
      </div>
      <div className="mt-4">
        <h3 className="text-sm font-medium text-gray-600">{title}</h3>
        <p className="text-2xl font-bold text-gray-900 mt-1">
          {typeof value === 'number' ? formatters.number(value) : value}
        </p>
      </div>
    </div>
  )
}

export default Overview 